import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import { useCallback, useState } from 'react';
import { ADGroupSearch, CloudGroupSearch, EUGroupSearch } from '../../../_api';
import { DataTable } from '../../../../../components/UI/MDC/DataTable/DataTable';
import Spinner from '../../../../../components/UI/Spinner/Spinner';
import { ADGroup, CloudGroup, EUGroup } from 'Classes/ResponseModels/Apps';
import { AD_TYPES } from 'pages/ApplicationMaintenance/_constants';

/**
 * @typedef {Object} SearchResultsProps
 * @property {ADGroup[]|CloudGroup[]|EUGroup[]} options
 * @property {AD_TYPES.CLOUD|AD_TYPES.EU|AD_TYPES.ON_PREM} type
 * @property {boolean} isLoading
 * @property {Function} handleSelect
 * @param {SearchResultsProps} props
 */
const SearchResults = ({ options, type, isLoading, handleSelect }) => {
	const onSelectHandler = useCallback(
		(_, __, row) => {
			const option = options.find(el => el.id.toString() === row.Key);
			handleSelect(option);
		},
		[options, handleSelect]
	);

	const actions = [{ Type: 'toggle', onChangedHandler: onSelectHandler }];
	const headers = [
		{ Value: 'ID', props: { sort: null, onSortChange: null } },
		{
			Value:
				type === AD_TYPES.CLOUD ? 'Display Name' : 'SAM Account Name',
			props: { sort: null, onSortChange: null },
		},
	];
	const rows = options.map(option => {
		return {
			ActionCells: [
				{
					BindTo: 0,
					Text: 'Select',
				},
			],
			Cells: {
				ID: {
					Value: option.id,
					Validation: {
						Key: 'true',
						Value: null,
						memberToValidateKey: null,
					},
				},
				[type === AD_TYPES.CLOUD ? 'Display_Name' : 'SAM_Account_Name']:
					{
						Value:
							type === AD_TYPES.CLOUD
								? option.displayName
								: option.samAccountName,
						Validation: {
							Key: 'true',
							Value: null,
							memberToValidateKey: null,
						},
					},
			},
			Key: option.id.toString(),
		};
	});

	return (
		<>
			{isLoading ? (
				<Spinner />
			) : (
				<DataTable actions={actions} rows={rows} headers={headers} />
			)}
		</>
	);
};

const ADGroupsSearch = ({ type, handleSelect }) => {
	const [searchTerm, setSearchTerm] = useState(null);
	/**
	 * @type {[ADGroup[]|CloudGroup[]|EUGroup[], Function]}
	 */
	const [options, setOptions] = useState([]);
	const [isLoading, setIsloading] = useState(false);
	const handleSearchChange = e => {
		setSearchTerm(e.target.value);
	};

	const handleSearch = (e, searchTerm) => {
		e.preventDefault();
		setIsloading(true);

		switch (type) {
			case AD_TYPES.CLOUD:
				CloudGroupSearch(searchTerm, false)
					.then(data =>
						setOptions(
							Array.isArray(data)
								? data.map(group => new CloudGroup(group))
								: []
						)
					)
					.then(() => setIsloading(false));
				break;
			case AD_TYPES.EU:
				EUGroupSearch(searchTerm)
					.then(data =>
						setOptions(
							Array.isArray(data)
								? data.map(group => new EUGroup(group))
								: []
						)
					)
					.then(() => {
						setIsloading(false);
					});
				break;
			case AD_TYPES.ON_PREM:
				ADGroupSearch(searchTerm)
					.then(data =>
						setOptions(
							Array.isArray(data)
								? data.map(group => new ADGroup(group))
								: []
						)
					)
					.then(() => {
						setIsloading(false);
					});
				break;
			default:
				break;
		}
	};

	return (
		<>
			<form onSubmit={e => handleSearch(e, searchTerm)}>
				<FormControl sx={{ paddingBottom: '16px' }}>
					<TextField
						variant='outlined'
						placeholder={`Search ${
							type === AD_TYPES.CLOUD
								? 'Cloud Groups...'
								: type === AD_TYPES.EU
								? 'EU Groups...'
								: 'AD Groups...'
						}`}
						onChange={handleSearchChange}
						id='ADGroupSearch'
						value={searchTerm}
						InputProps={{
							endAdornment: (
								<InputAdornment position='end'>
									<IconButton
										size='small'
										onClick={e =>
											handleSearch(e, searchTerm)
										}
										onKeyDown={e => {
											if (
												e.code === 'Enter' ||
												e.code === 'Space'
											) {
												handleSearch(e, searchTerm);
											}
										}}
										edge='end'
									>
										<SearchIcon />
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
				</FormControl>
			</form>
			<SearchResults
				type={type}
				options={options}
				isLoading={isLoading}
				handleSelect={handleSelect}
			/>
		</>
	);
};

export default ADGroupsSearch;
